import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { GripVertical, Info, MessageCircle, Trash2, ChevronDown } from 'lucide-react';
import StaticExerciseImage from '../../StaticExerciseImage';
import SetEditorInline from './SetEditorInline';
import { exerciseName } from '../../../lib/exerciseDisplay';
import { translateMuscle } from '../../../lib/muscleTranslations';

export default function RoutineExerciseCard({
  id, exercise, language, exerciseMuscleMap = {}, exerciseImages = {}, isCoachOrAdmin, setTypeColors = {},
  onInfo, onExchange, onEditCue, onDelete,
  onAddSet, onRemoveSet, onChangeSetType, onChangeField,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id, data: { type: 'card' },
  });
  const style = { transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const ex = exercise;
  const setCount = Array.isArray(ex.sets) ? ex.sets.length : 0;
  const workSets = (ex.sets || []).filter(s => s.set_type === 'work' || s.set_type === 'backoff').length;
  const url = exerciseImages[ex.name] || ex.gifUrl || ex.image_url;
  const primary = (exerciseMuscleMap[ex.name]?.targetMuscles?.length ? exerciseMuscleMap[ex.name].targetMuscles : ex.targetMuscles) || [];

  return (
    <div ref={setNodeRef} style={style}
      className="rounded-xl border border-border/40 bg-card hover:border-primary/40 transition-colors group"
      data-testid={`board-exercise-${ex.id}`}>
      <div className="flex items-center gap-2 p-2">
        <button {...attributes} {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-primary flex-shrink-0 touch-none"
          data-testid={`board-drag-${ex.id}`} aria-label={t('routineEditor.reorder')}>
          <GripVertical className="h-4 w-4" />
        </button>
        <button onClick={onInfo} className="w-9 h-9 rounded-lg overflow-hidden bg-muted flex-shrink-0" data-testid={`board-exercise-img-${ex.id}`}>
          <StaticExerciseImage src={url} alt={ex.name} size="sm" />
        </button>
        <button onClick={onInfo} className="flex-1 min-w-0 text-left" data-testid={`board-exercise-open-${ex.id}`}>
          <p className="font-semibold text-xs leading-tight truncate">{exerciseName(ex, language)}</p>
          <p className="text-[11px] text-muted-foreground truncate capitalize">
            {primary.map(m => translateMuscle(m, language)).join(', ') || translateMuscle(ex.muscle_group, language)}
          </p>
        </button>
        <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button onClick={onInfo} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary" title={t('tooltips.exerciseInfo')} data-testid={`board-info-${ex.id}`}>
            <Info className="h-3.5 w-3.5" />
          </button>
          <button onClick={onExchange} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary" title={t('tooltips.exchangeExercise')} data-testid={`board-exchange-${ex.id}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
            </svg>
          </button>
          {isCoachOrAdmin && (
            <button onClick={onEditCue} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary" title={t('techniqueCues.editCue')} data-testid={`board-cue-${ex.id}`}>
              <MessageCircle className="h-3.5 w-3.5" />
            </button>
          )}
          <button onClick={onDelete} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive" title={t('tooltips.delete')} data-testid={`board-delete-${ex.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Collapsible sets tab */}
      <button onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-1.5 px-2.5 py-1.5 border-t border-border/30 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors rounded-b-xl"
        data-testid={`board-toggle-sets-${ex.id}`} aria-expanded={expanded}>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        <span>
          {setCount} {setCount === 1 ? t('routineEditor.setLabel') : t('routineEditor.sets')}
          {workSets > 0 && workSets !== setCount ? <span className="text-primary"> · {workSets} {t('routineEditor.work')}</span> : null}
        </span>
      </button>

      {expanded && (
        <div className="p-2 pt-0">
          <SetEditorInline
            exercise={ex} language={language} setTypeColors={setTypeColors}
            onAddSet={onAddSet} onRemoveSet={onRemoveSet}
            onChangeSetType={onChangeSetType} onChangeField={onChangeField} />
        </div>
      )}
    </div>
  );
}
